import React from "react";
import { Box, useTheme, useMediaQuery, Skeleton, Tooltip, Typography } from "@mui/material";
import { getFlatPortfolioColors } from "../../../../utils/getFlatPortfolioColors";
import { roundedPanelSx } from "../../overviewPanel";

interface DividendSummaryMetricsProps {
  currentYield: string | null;
  payoutRatio: string | null;
  latestDividend: string | null;
  annualDividend: string | null;
  frequency: string | null;
  isLoading?: boolean;
}

interface MetricItemProps {
  label: string;
  value: string;
  valueColor?: string;
  isLoading?: boolean;
  tooltip?: string;
}

const MetricItem: React.FC<MetricItemProps> = ({ label, value, valueColor, isLoading, tooltip }) => {
  const theme = useTheme();
  const colors = getFlatPortfolioColors(theme);

  const content = (
    <Box sx={{ textAlign: "center", minWidth: 0, flex: 1, cursor: tooltip ? "help" : "default" }}>
      <Typography
        sx={{
          fontSize: "0.65rem",
          fontWeight: 500,
          color: colors.text.secondary,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      {isLoading ? (
        <Skeleton variant="text" width={60} height={28} sx={{ mx: "auto" }} />
      ) : (
        <Typography
          sx={{
            fontSize: { xs: "0.9rem", sm: "1.25rem" },
            fontWeight: 700,
            color: valueColor ?? colors.text.primary,
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>
      )}
    </Box>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow placement="top">
        {content}
      </Tooltip>
    );
  }

  return content;
};

export const DividendSummaryMetrics: React.FC<DividendSummaryMetricsProps> = ({
  currentYield,
  payoutRatio,
  latestDividend,
  annualDividend,
  frequency,
  isLoading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const colors = getFlatPortfolioColors(theme);

  // Determine payout ratio health color
  const getPayoutColor = () => {
    if (!payoutRatio) return colors.text.primary;
    const ratio = parseFloat(payoutRatio);
    if (ratio < 0) return colors.functional.error;
    if (ratio > 80) return colors.functional.warning;
    return colors.functional.success;
  };

  return (
    <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
      <Box
        sx={{
          display: "flex",
          gap: { xs: 0.5, sm: 4 },
          justifyContent: isMobile ? "space-around" : "space-between",
          width: "100%",
        }}
      >
        <MetricItem
          label="Payment Frequency"
          value={frequency ?? "N/A"}
          isLoading={isLoading}
        />
        <MetricItem
          label="Dividend Yield"
          value={currentYield ? `${currentYield}%` : "N/A"}
          valueColor={
            currentYield && parseFloat(currentYield) > 0
              ? colors.functional.success
              : undefined
          }
          isLoading={isLoading}
          tooltip="Annual dividend return based on today's share price, derived from the most recent regular dividend."
        />
        <MetricItem
          label="Payout Ratio"
          value={payoutRatio ? `${payoutRatio}%` : "N/A"}
          valueColor={getPayoutColor()}
          isLoading={isLoading}
          tooltip="Based on the most recent quarter. This ratio can fluctuate significantly depending on quarterly income levels."
        />
        <MetricItem
          label="Latest Payment"
          value={latestDividend ?? "N/A"}
          isLoading={isLoading}
        />
        <MetricItem
          label="Annual Payments"
          value={annualDividend ?? "N/A"}
          isLoading={isLoading}
        />
      </Box>
    </Box>
  );
};

export default DividendSummaryMetrics;
